/**
 * ProductDetailBookingCalendarSection — booking calendar section.
 *
 * PR fix (Founder directive 2026-11-03): "el calendario le está apareciendo
 * a los productos y creo que debería aparecerle solo a servicios". Pre-fix,
 * the calendar showed for ALL product types (physical, digital, service)
 * when the seller had bookingConfig.calendarEnabled=true. Post-fix: the
 * calendar ONLY shows for `type==='service'` products.
 */

'use client';

import { toast } from 'sonner';
import type { PublicProductResponse } from '@profileia/shared';
import { BookingCalendar } from '@/components/market/booking-calendar';

export function ProductDetailBookingCalendarSection({ product }: { product: PublicProductResponse }) {
  // PR fix: the calendar was not showing because it checked
  // `bookingConfig?.calendarEnabled` from the seller's fulfillmentOptions,
  // but many sellers never configured bookingConfig (it's per-seller).
  // The product's own `serviceConfig.calendarEnabled` is the source of
  // truth for whether THIS product has a calendar.
  // When the seller HAS bookingConfig, use it for deposit/checkIn/checkOut.
  // When the seller DOESN'T have bookingConfig, fall back to the product's
  // serviceConfig values.
  const sellerBookingConfig = product.seller?.fulfillmentOptions?.bookingConfig;
  const serviceConfig = product.serviceConfig;
  const calendarEnabled = serviceConfig?.calendarEnabled === true ||
    sellerBookingConfig?.calendarEnabled === true;

  // PR fix: only show calendar for SERVICE products.
  if (product.type !== 'service') return null;
  if (!calendarEnabled) return null;

  // Build bookingConfig from seller config OR product serviceConfig
  const depositPercent = sellerBookingConfig?.depositPercent ?? serviceConfig?.depositPercent ?? 50;
  const cancellationPolicy = sellerBookingConfig?.cancellationPolicy ?? serviceConfig?.cancellationPolicy ?? 'free_48h';
  const checkInTime = sellerBookingConfig?.checkInTime ?? '';
  const checkOutTime = sellerBookingConfig?.checkOutTime ?? '';

  return (
    <section className="mt-8" aria-label="Agenda de reservas">
      <h2 className="text-xl font-semibold mb-4">Agenda tu reserva</h2>
      <div className="rounded-lg border border-border bg-card p-5">
        <BookingCalendar
          productId={product.productId}
          pricePerNight={product.priceCredits}
          depositPercent={depositPercent}
          cancellationPolicy={cancellationPolicy}
          checkInTime={checkInTime}
          checkOutTime={checkOutTime}
          onBookingCreated={(bookingId) => {
            toast.success('Reserva confirmada', {
              description:
                `Tu reserva #${bookingId.slice(0, 8)} fue creada. ` +
                'Te enviaremos los detalles por email.',
              duration: 6000,
            });
            if (typeof window !== 'undefined') {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
        />
      </div>
    </section>
  );
}
